import { Add, ContentCopy, Delete, Edit, FilterAlt, Refresh, Warning } from "@mui/icons-material";
import { Grid, IconButton } from "@mui/material";
import DataGrid from '../../../../components/DataGrid';
import ContentTitle from "../../../../components/Content/ContentTitle";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import { useEffect, useCallback } from "react";
import EnterpriseService from "../../../../services/EnterpriseService";
import FormHelper from "../../../../helpers/FormHelper";
import RegistrationStatus from "../../../../components/RegistrationStatus";
import AddEditDialog from "./AddEditDialog";
import { useAuthContext } from "../../../../contexts/AuthContext";
import RemoveDialog from "./RemoveDialog";
import DefaultConstants from "../../../../data/Constants";
import IEnterprise from "../../../../interfaces/IEnterprise";
import DateTimeHelper from "../../../../helpers/DateTimeHelper";
import { INavigationLink } from "../../../../interfaces/INavigation";
import { useEnvironmentContext } from "../../../../contexts/EnvironmentContext";
import Filters from "./Filters";
import { RegistrationStatusEnum } from "../../../../data/Enums";
import Tooltip from "../../../../components/Tooltip";
import useDrawer from "../../../../hooks/useDrawer";
import useDialog from "../../../../hooks/useDialog";
import useState from "../../../../hooks/useState";


interface IStateValues {
    filters: FormData | undefined;
    data: IEnterprise[];
    selectedRow: IEnterprise | undefined;
}

const Enterprise = (linkProps: INavigationLink) => {
    const { translate } = useLanguageContext();
    const { environment } = useEnvironmentContext();
    const { user, hasPermission } = useAuthContext();

    const state = useState<IStateValues>({
        filters: undefined,
        data: [],
        selectedRow: undefined,
    });

    const filtersDrawer = useDrawer();
    const addEditDialog = useDialog();
    const removeDialog = useDialog();


    useEffect(() => {
        handleRefresh();
        // eslint-disable-next-line
    }, [state.values.filters]);
    
    
    const handleRefresh = useCallback(() => {
        const formData = state.values.filters ?? FormHelper.Create(undefined);

        EnterpriseService.List(formData)
            .then((resp) => {
                state.update('data', resp.data.enterprises as IEnterprise[]);
            });
        
        // eslint-disable-next-line
    }, [state.values.filters]);

    const handleAddEdit = useCallback((row: IEnterprise | undefined) => {
        state.update('selectedRow', row);
        addEditDialog.handleOpen();

        // eslint-disable-next-line
    }, []);

    const handleRemove = useCallback((row: IEnterprise) => {
        state.update('selectedRow', row);
        removeDialog.handleOpen();

        // eslint-disable-next-line
    }, []);


    const dataGridColumns = [
        { 
            field: 'name', 
            headerName: translate('name'),
            flex: 1, 
            minWidth: 200
        }, { 
            field: 'databaseTypeId',  
            headerName: translate('database'), 
            flex: 1, 
            minWidth: 200,
            renderCell: (params: any) => {
                const databaseType = environment.databaseTypeList.find(d => d.id === params.row.databaseTypeId);

                if (!databaseType) {
                    return translate('undefined');
                }
                
                return databaseType.description;
            }
        }, { 
            field: 'partnerId',  
            headerName: translate('partnerId'), 
            flex: 1, 
            minWidth: 200,
            renderCell: (params: any) => {
                if (!params.row.partnerId) {
                    if (params.row.administrator) {
                        return translate('undefined');
                    }

                    return <Tooltip title={translate('_enterprisePartnerIdEmpty')} placement="bottom">
                        <IconButton className='actionbutton-warning marginX-none'>
                            <Warning fontSize='small' />
                        </IconButton>
                    </Tooltip>;
                }
                
                return params.row.partnerId;
            }
        }, { 
            field: 'registrationStatusId', 
            type: DefaultConstants.dataGridRegistrationStatusType, 
            headerName: translate('situation'), 
            flex: 1, 
            minWidth: 200, 
            renderCell: (params: any) => {
                return <RegistrationStatus id={params.row.registrationStatusId} />;
            }
        }, { 
            field: 'expiresAt', 
            headerName: translate('expiresAt'), 
            flex: 1, 
            minWidth: 200, 
            renderCell: (params: any) => {
                const date = DateTimeHelper.Format(params.row.expiresAt);
                const diff = DateTimeHelper.Diff(params.row.expiresAt, DateTimeHelper.Now());
                
                if (diff < 0) {
                    return <span className='color-red'>
                        {date} ({translate('expired').toLowerCase()})
                    </span>;
                } else if (diff < 5) {
                    let sulfix = diff === 0 ? 'today' : (diff === 1 ? 'tomorrow' : 'days');
                    sulfix = translate(sulfix).toLowerCase();

                    if (diff > 1) {
                        sulfix = `${diff} ${sulfix}`;
                    }

                    return <span className='color-amber'>
                        {date} ({sulfix})
                    </span>;
                }

                return date;
            }
        }, { 
            field: 'actions', 
            type: 'actions', 
            headerName: translate('actions'), 
            width: 200, 
            renderCell: (params: any) => {
                if (params.row.administrator && !user?.administrator) {
                    return;
                }

                if (Number(params.row.registrationStatusId) === RegistrationStatusEnum.Excluded) {
                    return;
                }

                return (
                    <Grid item>
                        {
                            hasPermission(`${linkProps.alias[0]}.Add`) && (
                                <Tooltip title={translate('clone')}>
                                    <IconButton 
                                        className='actionbutton-clone' 
                                        onClick={() => handleAddEdit({
                                            ...params.row, 
                                            id: 0,
                                            name: '',
                                            token: '',
                                        })}
                                    >
                                        <ContentCopy fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }

                        {
                            hasPermission(`${linkProps.alias[0]}.Edit`) && (
                                <Tooltip title={translate('edit')}>
                                    <IconButton className='actionbutton-edit' onClick={() => handleAddEdit(params.row)}>
                                        <Edit fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }

                        {
                            !params.row.administrator && hasPermission(`${linkProps.alias[0]}.Remove`) && (
                                <Tooltip title={translate('remove')}>
                                    <IconButton className='actionbutton-remove' onClick={() => handleRemove(params.row)}>
                                        <Delete fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                    </Grid>
                );
            }
        }
    ];

    return (
        <>
            <Grid item xs={12} className='grid-header'>
                <Grid item className='grid-title'>
                    <ContentTitle {...linkProps} />
                </Grid>

                <Grid item className='grid-tools'>
                    {
                        hasPermission(`${linkProps.alias[0]}.Add`) && (
                            <Tooltip title={translate('add')}>
                                <IconButton onClick={() => handleAddEdit(undefined)}>
                                    <Add />
                                </IconButton>
                            </Tooltip>
                        )
                    }
                    
                    <Tooltip title={translate('filter')}>
                        <IconButton onClick={() => filtersDrawer.handleOpen()}>
                            <FilterAlt />
                        </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={translate('refresh')}>
                        <IconButton onClick={handleRefresh}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            <Grid container className='grid-content'>
                <Grid item xs={12} className='paper-content'>
                    <DataGrid 
                        columns={dataGridColumns} 
                        data={state.values.data} 
                        openSearch={false}
                        onCellDoubleClick={params => {
                            if (params.row.administrator && !user?.administrator) {
                                return;
                            }

                            if (Number(params.row.registrationStatusId) === RegistrationStatusEnum.Excluded) {
                                return;
                            }

                            if (hasPermission(`${linkProps.alias[0]}.Edit`)) {
                                handleAddEdit(params.row);
                                return;
                            }
                            
                            if (hasPermission(`${linkProps.alias[0]}.Remove`)) {
                                handleRemove(params.row);
                                return;
                            }
                        }}
                    />
                </Grid>
            </Grid>

            <Filters
                open={filtersDrawer.open}
                handleClose={() => filtersDrawer.handleClose()}
                handleApplyFilters={formData => state.update('filters', formData)}
            />

            <AddEditDialog
                open={addEditDialog.open}
                handleClose={() => addEditDialog.handleClose()}
                data={state.values.selectedRow}
                handleListRefresh={handleRefresh}
            />

            <RemoveDialog
                open={removeDialog.open}
                handleClose={() => removeDialog.handleClose()}
                data={state.values.selectedRow}
                handleListRefresh={handleRefresh}
            />
        </>
    );
}

export default Enterprise;